// The 51-character textfen (spec §2): side to move, then squares 1..50 as
// w/b (men), W/B (kings), _ (empty). Plus the 10×10 geometry for drawing.
// This file applies moves the server or the WASM generator produced; it never
// decides what is legal.
import type { LegalMove } from "../api/client";

export type PieceKind = "w" | "b" | "W" | "B";
export type Piece = { sq: number; kind: PieceKind };

export function parse(textfen: string): Piece[] {
  const out: Piece[] = [];
  for (let sq = 1; sq <= 50; sq++) {
    const c = textfen[sq];
    if (c === "w" || c === "b" || c === "W" || c === "B") out.push({ sq, kind: c });
  }
  return out;
}

export function sideToMove(textfen: string): "W" | "B" {
  return textfen[0] === "B" ? "B" : "W";
}

export function apply(textfen: string, move: Pick<LegalMove, "from" | "to" | "captured" | "promotion">): string {
  const b = textfen.split("");
  let piece = b[move.from];
  b[move.from] = "_";
  for (const c of move.captured) b[c] = "_";
  if (move.promotion) piece = piece.toUpperCase();
  b[move.to] = piece;
  b[0] = textfen[0] === "W" ? "B" : "W";
  return b.join("");
}

/** Row 0..9 from the top and column 0..9 from the left, white at the bottom. */
export function rowCol(sq: number, flipped = false): [number, number] {
  const i = sq - 1;
  const row = Math.floor(i / 5);
  const col = row % 2 === 0 ? 2 * (i % 5) + 1 : 2 * (i % 5);
  return flipped ? [9 - row, 9 - col] : [row, col];
}

export function squareAt(row: number, col: number, flipped = false): number | null {
  if (flipped) {
    row = 9 - row;
    col = 9 - col;
  }
  if (row < 0 || row > 9 || col < 0 || col > 9) return null;
  if ((row + col) % 2 === 0) return null; // light square
  return row * 5 + Math.floor(col / 2) + 1;
}

/** Centre of a square in board units (the board is 1000 × 1000). */
export function centre(sq: number, flipped = false): [number, number] {
  const [r, c] = rowCol(sq, flipped);
  return [c * 100 + 50, r * 100 + 50];
}

export function fenToTextfen(fen: string): string {
  const parts = fen.replace(/"/g, "").trim().split(":");
  const b = Array(51).fill("_");
  b[0] = parts[0].toUpperCase() === "B" ? "B" : "W";
  for (const part of parts.slice(1)) {
    const colour = part[0]?.toUpperCase();
    for (const item of part.slice(1).split(",")) {
      const t = item.trim();
      if (!t) continue;
      const king = t[0] === "K" || t[0] === "k";
      const sq = Number(king ? t.slice(1) : t);
      if (sq >= 1 && sq <= 50) {
        const ch = colour === "W" ? "w" : "b";
        b[sq] = king ? ch.toUpperCase() : ch;
      }
    }
  }
  return b.join("");
}

export function moveText(m: Pick<LegalMove, "from" | "to" | "captured">): string {
  return `${m.from}${m.captured.length ? "x" : "-"}${m.to}`;
}
